import { Editor, Transforms, Text } from 'slate';
import { jsx } from 'slate-hyperscript';

export const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const ELEMENT_TAGS = {
  A: (el) => ({ type: 'link', url: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  PRE: () => ({ type: 'code' }),
  UL: () => ({ type: 'bulleted-list' }),
};

export const TEXT_TAGS = {
  B: () => ({ bold: true }),
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  INS: () => ({ underline: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

export const deserialize = (el) => {
  if (el.nodeType === 3) {
    return el.textContent;
  }
  if (el.nodeType !== 1) {
    return null;
  }
  if (el.nodeName === 'BR') {
    return '\n';
  }

  const { nodeName } = el;
  let parent = el;

  if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
    [parent] = el.childNodes;
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat();

  if (children.length === 0) {
    children = [{ text: '' }];
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    return jsx('element', attrs, children);
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);
    return children.map((child) => jsx('text', attrs, child));
  }

  return children;
};

const EMPTY_PARAGRAPH = { type: 'paragraph', children: [{ text: '' }] };

const isInlineNode = (node) => Text.isText(node) || node?.type === 'link';

const isBlankText = (node) => Text.isText(node) && !node.text?.trim();

const isEmptyParagraph = (node) =>
  node?.type === 'paragraph' && node?.children?.length === 1 && !node.children[0]?.text;

export const wrapOrphanNodes = (nodes) => {
  const result = [];
  let buffer = [];

  const flush = () => {
    if (!buffer.length) return;
    result.push({ type: 'paragraph', children: buffer });
    buffer = [];
  };

  const hasBlockNode = nodes.some((node) => !isInlineNode(node));
  const relevantNodes = hasBlockNode ? nodes.filter((node) => !isBlankText(node)) : nodes;

  relevantNodes.forEach((node) => {
    if (!isInlineNode(node)) {
      flush();
      result.push(node);
      return;
    }

    if (Text.isText(node) && node.text?.includes('\n')) {
      node.text.split('\n').forEach((text, index) => {
        if (index > 0) flush();
        buffer.push({ ...node, text });
      });
      return;
    }

    buffer.push(node);
  });

  flush();

  while (result.length > 1 && isEmptyParagraph(result[result.length - 1])) {
    result.pop();
  }

  return result.length ? result : [EMPTY_PARAGRAPH];
};

export const deserializeToSlate = (html?: string) => {
  const parsed = new DOMParser().parseFromString(html || '<p></p>', 'text/html');
  const fragment = deserialize(parsed.body);

  return wrapOrphanNodes(Array.isArray(fragment) ? fragment : []);
};

export const withHtml = (editor) => {
  const { insertData, isInline, isVoid } = editor;

  editor.isInline = (element) => (element.type === 'link' ? true : isInline(element));

  editor.isVoid = (element) => (element.type === 'image' ? true : isVoid(element));

  editor.insertData = (data) => {
    const html = data.getData('text/html');

    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fragment = deserialize(parsed.body);
      Transforms.insertFragment(editor, fragment);
      return;
    }

    insertData(data);
  };

  return editor;
};

/**
 * Verifica se o conteúdo HTML de um editor de texto está vazio.
 * Considera como vazio conteúdos que contêm apenas tags <p> vazias e quebras de linha.
 *
 * @param htmlContent O conteúdo HTML a ser verificado
 * @returns true se o conteúdo for considerado vazio, false caso contrário
 *
 * @example
 * // Retorna true para os seguintes casos:
 * isRichTextEmpty("<p></p>")
 * isRichTextEmpty("<p></p>\n")
 * isRichTextEmpty("<p></p><p></p>")
 * isRichTextEmpty("<p></p>\n<p></p>")
 * isRichTextEmpty("<p>&nbsp;</p>")
 * isRichTextEmpty("<p><br></p>")
 */
export function isRichTextEmpty(htmlContent: string | undefined | null): boolean {
  // Retorna true se o conteúdo for undefined, null ou string vazia
  if (!htmlContent) return true;

  // Remove espaços em branco, quebras de linha e caracteres especiais HTML comuns
  const cleanedContent = htmlContent
    .replace(/<p>(&nbsp;|<br>|\s)*<\/p>/g, '') // Remove parágrafos vazios com possíveis espaços ou <br>
    .replace(/\n/g, '') // Remove quebras de linha
    .replace(/<\/?[^>]+(>|$)/g, '') // Remove todas as tags HTML
    .trim(); // Remove espaços em branco no início e no fim

  // Se o conteúdo limpo estiver vazio, o texto rico é considerado vazio
  return cleanedContent.length === 0;
}
